import { useEffect, useState } from "react";

// React DOM
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";

// Icons and Backgrounds
import SocialMedia from "../../components/SocialMedia";

// Axios
import Axios from "../../api/axios";

// CSS Styling
import "../../styles/detailBlog.css";

// HTML react parser
import parse, { attributesToProps, domToReact } from "html-react-parser";

// Sweet Alert v2
import Swal from "sweetalert2";

// react responsive
import { useMediaQuery } from "react-responsive";

// Components
import Seo, { organizationRef } from "../../components/Seo";
import ContainerText from "../../components/Container";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

// Helpers
import { absoluteUrl, blogPath, isoDate, newsTitle, plainText } from "../../helpers/seo";

// API
import { getNewsById } from "../../api/getNewsById";

// React lottie
import Lottie from "react-lotties";
import PaperPlane from "../../lotties/paperplane.json";

// Lucide React
import { CircleUserRound } from "lucide-react";

// The page already has its own <h1>, so an <h1> written in the CMS content becomes an <h2> that looks the same
const parseContent = (html) =>
  parse(html, {
    replace: (node) => {
      if (node.type === "tag" && node.name === "h1") {
        return (
          <h2 {...attributesToProps(node.attribs)} className="h1">
            {domToReact(node.children)}
          </h2>
        );
      }
    },
  });

const DetailBlog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isMobileSize = useMediaQuery({ query: "(max-width: 640px)" });

  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [activeLanguage, setActiveLanguage] = useState("en");

  // Empty object until the post has loaded, so the markup below can read fields safely
  const post = news || {};

  // React lotties
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PaperPlane,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const HTMLparse = (string) => {
    return parseContent(string);
  };

  // Canonical address of the post, used for sharing and copying the link
  const shareUrl = absoluteUrl(blogPath(post));

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    Swal.fire({
      position: "center",
      imageUrl:
        "https://cdn.discordapp.com/attachments/796711355876245534/986824551537209404/copy_clipboard_icon.png",
      imageWidth: 200,
      imageHeight: 200,
      width: "45em",
      title: "Success",
      text: "Link has been copied to clipboard",
      showCloseButton: true,
      showConfirmButton: false,
      timer: 1500,
      background: "#E4E4E4",
    });
  };

  const fetchRelatedNews = async () => {
    const response = await Axios.get(`/news/relatedNews`).catch((err) => {
      console.log("Err: ", err);
    });
    if (response && Array.isArray(response.data)) setRelatedNews(response.data);
  };

  useEffect(() => {
    // Ignore a response that arrives after the post id changed or the page was left
    let cancelled = false;
    const fetchNewsById = async () => {
      setLoading(true);
      setNotFound(false);
      setNews(null);
      setActiveLanguage("en");
      window.scrollTo(0, 0);
      try {
        const response = await getNewsById(id);
        if (cancelled) return;
        if (response.data.status === "Published") {
          setNews(response.data);
          HTMLparse(response.data.htmlContent);
          fetchRelatedNews();
        } else {
          setNotFound(true);
          Swal.fire(`Oops`, `Data not found`, `error`).then(() =>
            navigate("/blog")
          );
        }
      } catch (err) {
        console.log(err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchNewsById();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Old /Blog/7 links (and posts whose title changed) move to the canonical /Blog/7/slug url.
  // It only runs once the post is loaded and stops as soon as the path matches.
  useEffect(() => {
    // news.id must match the url id: right after a link click the previous post is still in state
    if (
      news &&
      String(news.id) === String(id) &&
      location.pathname !== blogPath(news)
    ) {
      navigate(blogPath(news), { replace: true });
    }
  }, [news, id, location.pathname]);

  // Title shown by default: English when there is one, else Indonesian
  const defaultIsEnglish = !!post.title_en;
  const displayTitle =
    post.title_en && activeLanguage === "en"
      ? post.title_en
      : post.title_idn && activeLanguage === "id"
      ? post.title_idn
      : post.title_en || post.title_idn;
  const summary = plainText(
    defaultIsEnglish
      ? post.description_en || post.description_idn
      : post.description_idn || post.description_en,
    160
  );
  // Very short summaries (some posts share one) fall back to the article text.
  const seoDescription =
    summary.length >= 70 ? summary : plainText(post.htmlContent, 160) || summary;
  const seoImage = post.picture || post.content_image;

  return (
    <div id="detailBlog">
      {news && news.id ? (
        <Seo
          title={newsTitle(post)}
          description={seoDescription}
          path={blogPath(post)}
          image={seoImage}
          imageAlt={newsTitle(post)}
          type="article"
          lang={defaultIsEnglish ? "en" : "id"}
          breadcrumbs={[
            { name: "Blog", path: "/blog" },
            { name: newsTitle(post), path: blogPath(post) },
          ]}
          article={{
            publishedTime: isoDate(post.createdAt),
            modifiedTime: isoDate(post.updatedAt),
            author: post.written_by,
            section: post.contentType,
          }}
          jsonLd={{
            "@type": "BlogPosting",
            headline: newsTitle(post),
            description: seoDescription,
            image: seoImage ? [absoluteUrl(seoImage)] : undefined,
            datePublished: isoDate(post.createdAt),
            dateModified: isoDate(post.updatedAt || post.createdAt),
            author: post.written_by
              ? { "@type": "Person", name: post.written_by }
              : undefined,
            publisher: organizationRef,
            mainEntityOfPage: absoluteUrl(blogPath(post)),
            inLanguage: defaultIsEnglish ? "en" : "id",
          }}
        />
      ) : notFound ? (
        <Seo title="Article not found" noindex />
      ) : null}
      <Header />
      {!isLoading ? (
        <ContainerText
          props={
            <div className="detailBlogContainer">
              <div className="d-flex align-items-center justify-content-between mb-4 mb-md-5">
                <div
                  className="previousButton"
                  onClick={() => navigate("/blog")}
                >
                  <span className="expertiseArrowRight">
                    <i className="fas fa-angle-left" />
                  </span>
                  <Link to="/blog" onClick={(e) => e.stopPropagation()}>
                    Back to previous page
                  </Link>
                </div>

                {post.title_idn && post.title_en ? (
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-check-label d-inline-flex align-items-center cursor-pointer me-3 me-sm-4">
                      <span className="fw-bold text-dark px-5 title">EN</span>

                      <div
                        onClick={() =>
                          setActiveLanguage(
                            activeLanguage === "en" ? "id" : "en"
                          )
                        }
                        className="form-check form-switch m-0 p-0"
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="langSwitch"
                          checked={activeLanguage === "id"}
                          onChange={() =>
                            setActiveLanguage(
                              activeLanguage === "en" ? "id" : "en"
                            )
                          }
                          style={{
                            width: "3.5rem",
                            height: "2rem",
                            cursor: "pointer",
                            backgroundColor: "#E78D41",
                          }}
                        />
                      </div>

                      <span className="ms-2 fw-bold text-dark title">ID</span>
                    </label>
                  </div>
                ) : post.title_idn && !post.title_en ? (
                  <div>
                    <p className="fw-medium mb-0 text-dark paragraf">
                      Artikel berikut hanya tersedia dalam Bahasa Indonesia
                    </p>
                  </div>
                ) : !post.title_idn && post.title_en ? (
                  <div>
                    <p className="fw-medium mb-0 text-dark paragraf">
                      This article is only available in English
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="detailBlogMainTitle headerDescription">
                <h1 className="seoH1 seoH1Inherit">{displayTitle}</h1>
              </div>
              <div className="paragrafDesc">
                <p>
                  <i>
                    {post.date} – {post.contentType}
                  </i>
                </p>
                <div className="d-flex align-items-center gap-2 gap-sm-3">
                  <div
                    className="rounded-circle overflow-hidden flex-shrink-0"
                    style={{
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    {post.written_by_img ? (
                      <img
                        src={post.written_by_img}
                        alt={`${post.written_by}`}
                        loading="lazy"
                        className="w-100 h-100 object-fit-cover rounded-circle img-fluid"
                      />
                    ) : (
                      <CircleUserRound size={40} />
                    )}
                  </div>

                  <div>
                    <p className="fw-medium mb-0 text-dark paragrafDesc">
                      Written by <b>{post.written_by}</b>
                    </p>
                  </div>
                </div>
              </div>
              <div className="detailBlogSocialMedia right">
                <SocialMedia
                  twitterURL={shareUrl}
                  facebookURL={shareUrl}
                  linkedinURL={shareUrl}
                  mailSubject={displayTitle}
                  mailContent={`Hello, I recommend you to read the articles on Institute for Sustainability and Agility (ISA): ${displayTitle} ${shareUrl}`}
                  copyLink={copyLink}
                />
              </div>
              <div className="detailBlogHtmlContent">
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobileSize ? "column" : "row",
                    justifyContent: "space-between",
                  }}
                >
                  {/* <div style={{ paddingRight: "3em" }}>
                    <img
                      src={news.content_image}
                      alt="Content Image"
                      style={{ width: "300 px", height: "300 px" }}
                    ></img>
                  </div> */}
                  <div style={{ marginTop: isMobileSize ? "0.5em" : "-0.5em" }}>
                    {HTMLparse(`${post.htmlContent}`)}
                  </div>
                </div>
              </div>
              <div className="detailBlogSocialMedia left">
                <SocialMedia
                  twitterURL={shareUrl}
                  facebookURL={shareUrl}
                  linkedinURL={shareUrl}
                  mailSubject={displayTitle}
                  mailContent={`Hello, I recommend you to read the articles on Institute for Sustainability and Agility (ISA): ${displayTitle} ${shareUrl}`}
                  copyLink={copyLink}
                />
              </div>
            </div>
          }
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            paddingTop: "15em",
          }}
        >
          <Lottie options={lottieOptions} height={200} width={400} />
          <h4
            style={{
              color: "#0D4C86",
            }}
          >
            LOADING
          </h4>
          <p
            style={{
              color: "#0D4C86",
            }}
          >
            Processing your request, please wait...
          </p>
        </div>
      )}
      {relatedNews.length > 0 ? (
        <div className="detailUpdates">
          <ContainerText
            props={
              <div className="detailUpdatesContainer">
                <div className="detailUpdatesMainTitle header">
                  <p>Related Updates</p>
                </div>
                <div className="detailUpdatesCardContainer">
                  {relatedNews.map((element) => {
                    return element.status === "Published" ? (
                      <div className="detailUpdatesCardEach" key={element.id}>
                        <div className="detailUpdatesCardImage">
                          <img
                            src={element.picture}
                            alt={newsTitle(element)}
                            loading="lazy"
                          />
                        </div>
                        <div className="detailUpdatesCardTitle title">
                          <p>
                            <Link to={blogPath(element)}>
                              {newsTitle(element)}
                            </Link>
                          </p>
                        </div>
                        <div className="detailUpdatesCardDate paragrafDesc">
                          <p>
                            <i>
                              {element.date} – {element.contentType}
                            </i>
                          </p>
                        </div>
                        <div className="detailUpdatesCardSubTitle paragraf">
                          <p>{element.description_en || element.description_idn || element.description}</p>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            }
          />
        </div>
      ) : null}
      <Footer />
    </div>
  );
};

export default DetailBlog;
